// @flow
import type { ElementConfig } from 'react';
import React from 'react';
import { I18n, Trans } from '@lingui/react';
import styled, { type StyledComponent } from 'styled-components';

import { Close } from '@rover/icons';
import { DSTokenMap, Spacing } from '@rover/kibble/styles';
import Button, { ButtonType } from '@rover/react-lib/components/buttons/Button';
import { ALERT_CLASSNAME } from '@rover/react-lib/constants/classNames.constants';
import { isTranslatable } from '@rover/utilities/i18n';

import {
  AlertSeverity,
  defaultProps as commonDefaultProps,
  getBackgroundColorForSeverity,
  getBorderColorForSeverity,
} from './Alert.common';
import type { Props as CommonProps } from './Alert.types';

export type Props = {
  ...CommonProps,
  className: string,
};

type WrapperProps = $Diff<Props, { onDismiss: () => void }>;

const Wrapper: StyledComponent<WrapperProps> = styled.div`
  display: flex;
  padding: ${Spacing.M};
  margin-bottom: ${Spacing.L};

  background-color: ${getBackgroundColorForSeverity};
  /* !important is needed to for-sure override the .alert class, which is needed by some scrolling logic */
  border-width: 2px !important;
  border-style: solid !important;
  border-color: ${getBorderColorForSeverity} !important;
  border-radius: 4px;
`;

const ChildWrapper: StyledComponent<{}> = styled.div`
  display: flex;
  flex: 1;
  color: ${DSTokenMap.TEXT_COLOR_PRIMARY};
`;

export const DismissIconWrapper: StyledComponent<ElementConfig<typeof Button>, {}, any> = styled(
  Button
)`
  display: flex;
  height: 16px;
  padding: 0;
  width: 16px;
`;

const DismissIcon: StyledComponent<{}> = styled(Close)`
  cursor: pointer;
  fill: ${DSTokenMap.TEXT_COLOR_TERTIARY};
  height: 16px;
  width: 16px;

  &:hover {
    fill: ${DSTokenMap.TEXT_COLOR_SECONDARY};
  }
`;

const renderDismissIcon = ({ onDismiss }: { onDismiss: () => void }) => {
  if (!onDismiss) return null;
  return (
    <I18n>
      {({ i18n }) => (
        <DismissIconWrapper
          tabIndex={0}
          role="button"
          aria-label={i18n._('Close Alert')}
          buttonType={ButtonType.TEXT}
          onClick={onDismiss}
        >
          <DismissIcon />
        </DismissIconWrapper>
      )}
    </I18n>
  );
};

const Alert = ({ children, onDismiss, severity, className, ...other }: Props) => {
  if (!children) return null;
  return (
    <Wrapper severity={severity} className={`${ALERT_CLASSNAME} ${className}`} {...other}>
      <ChildWrapper>{isTranslatable(children) ? <Trans id={children} /> : children}</ChildWrapper>
      {renderDismissIcon({ onDismiss })}
    </Wrapper>
  );
};

// attach AlertSeverity to default export for convenience
Alert.Severity = AlertSeverity;

Alert.defaultProps = {
  ...commonDefaultProps,
  className: '',
};

export { AlertSeverity };

export default Alert;



// WEBPACK FOOTER //
// ../react-lib/src/components/notifications/Alert/Alert.jsx